import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { CheckIcon, QueueListIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import Button from '@/components/atoms/button';
import Input from '@/components/atoms/input';
import TextArea from '@/components/atoms/text-area';
import toast from '@/components/atoms/toaster/helpers/toast';
import useDisclosure from '@/hooks/useDisclosure';
import useEntityToasters from '@/hooks/useEntityToasters';
import useHideZendeskWidget from '@/hooks/useHideZendeskWidget';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { classnames } from '@/utils/classnames/classnames';
import { PurchaseList } from '@/types/entity/purchase-list';
import { pushCreateList, pushAddToList } from '@/lib/analytics/dataLayer';
import { ShoppingListCTAProps, Wishlist } from '../types';
import WishlistToast from './wishlist-toast';

const ShoppingListCTA = ({
  getWishlists,
  canAddToOwnWishlist,
  canAddToOthersWishlist,
  addToWishlists,
  removeFromWishlists,
  addToNewWishlist,
  addToWishlistDisabled = false,
  productName,
  productSku,
  analyticsItem,
}: ShoppingListCTAProps) => {
  const translate = useTranslations();
  const titleId = useId();
  const formId = useId();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { showFailedMessage } = useEntityToasters('purchaselist');
  const { selectedStore } = useStoreAndBusinessUnits();

  const [lists, setLists] = useState<Array<Wishlist>>([]);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [addedId, setAddedId] = useState<string | null>(null);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const addedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  // EWM-665 (QA round 3) — this add-to-purchase-list panel is a side tray, so it
  // must NOT lock body scroll. It previously called `useScrollBlock` here, which
  // set `overflow-y: hidden` on the body (removing the page scrollbar) on every
  // viewport — the exact horizontal shift EWM-665 fixes, and one that bypassed
  // the shared Drawer's fix because this panel is hand-rolled. The page now stays
  // scrollable with its scrollbar visible while the panel is open (AC #2), like
  // the sign-in drawer. We still hide the Zendesk launcher while open — that was
  // a side effect of the old scroll lock — so its fixed bubble doesn't overlap
  // the panel's footer button.
  useHideZendeskWidget(isOpen);

  // Cancel the "Added!" flash timer on unmount.
  useEffect(
    () => () => {
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    },
    [],
  );

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Move focus into the drawer on open; restore it to the trigger on close.
  //
  // EWM-847 — the `else` branch must fire only on a real open -> close
  // transition. It used to run on MOUNT too (where `isOpen` is already false
  // and nothing has been closed), focusing the trigger on every PDP load — and
  // the browser scrolls a newly focused element into view. On mobile this
  // trigger sits ~1143px down the page, so signed-in dealers landed ~835px
  // down the PDP, past the header, breadcrumb and product image, staring at the
  // add-to-cart block. Desktop was unaffected only because the trigger falls
  // inside the first viewport there, and guests because this CTA doesn't render
  // for them.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    } else if (wasOpenRef.current) {
      triggerButtonRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Partial<PurchaseList>>();

  // EWM-760 (was a setState-in-effect on `[fetchLists]`, calling the memoized
  // `fetchLists` synchronously) — fetch the wishlists on mount / whenever the
  // role-gating props or `getWishlists` change. Inlined as an async IIFE
  // (rather than calling out to a separate `useCallback`) so the actual
  // `setLists` write stays inside the effect's own async callback — the
  // sanctioned "subscribe/fetch, then setState when data arrives" shape —
  // instead of a synchronous call to a local function that eventually
  // setStates, which is what the rule was flagging. Same dependencies as
  // before (deps of the old `fetchLists` plus `fetchLists` itself, which was
  // exactly those three), so this fires on the identical trigger.
  useEffect(() => {
    (async () => {
      const shoppingLists = ((await getWishlists()) ?? []).filter((shoppingList) =>
        shoppingList.isOwnWishlist ? canAddToOwnWishlist : canAddToOthersWishlist,
      );
      setLists(shoppingLists);
    })();
  }, [getWishlists, canAddToOwnWishlist, canAddToOthersWishlist]);

  const handleClose = () => {
    if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    setAddedId(null);
    onClose();
  };

  const handleToggle = async (id: string) => {
    const list = lists.find((l) => l.id === id);
    if (!list || loadingId) return;

    const wasInList = !!list.productIsInWishlist;

    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, productIsInWishlist: !wasInList } : l)));
    setLoadingId(id);

    try {
      if (wasInList) {
        await removeFromWishlists([{ wishlistId: id, lineItemId: list.lineItemId ?? '' }]);
      } else {
        await addToWishlists([id]);
        if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
        setAddedId(id);
        addedTimerRef.current = setTimeout(() => setAddedId(null), 2000);
        if (analyticsItem?.currency) {
          const listInfo = lists.find((l) => l.id === id);
          pushAddToList({
            item_list_id: id,
            item_list_name: listInfo?.label ?? id,
            item: {
              item_id: analyticsItem.item_id,
              item_name: analyticsItem.item_name,
              price: analyticsItem.price ?? 0,
              item_brand: analyticsItem.item_brand,
              item_category: analyticsItem.item_category,
            },
            quantity: 1,
            currency: analyticsItem.currency,
          });
        }
      }
    } catch {
      setLists((prev) => prev.map((l) => (l.id === id ? { ...l, productIsInWishlist: wasInList } : l)));
    } finally {
      setLoadingId(null);
    }
  };

  const handleCreateSubmit = async (data: Partial<PurchaseList>) => {
    setIsCreatingList(true);
    try {
      const listItem = await addToNewWishlist({ ...(data as PurchaseList), store: selectedStore });
      if (listItem?.name && listItem?.wishlistId) {
        toast.render(<WishlistToast wishlist={{ label: listItem.name, id: listItem.wishlistId }} />, 'success', {
          position: 'top-right',
        });
      }
      pushCreateList({
        item_list_id: listItem?.wishlistId ?? '',
        item_list_name: listItem?.name ?? '',
      });
      if (analyticsItem?.currency) {
        pushAddToList({
          item_list_id: listItem?.wishlistId ?? '',
          item_list_name: listItem?.name ?? '',
          item: {
            item_id: analyticsItem.item_id,
            item_name: analyticsItem.item_name,
            price: analyticsItem.price ?? 0,
            item_brand: analyticsItem.item_brand,
            item_category: analyticsItem.item_category,
          },
          quantity: 1,
          currency: analyticsItem.currency,
        });
      }
      reset();
      handleClose();
    } catch {
      showFailedMessage();
    } finally {
      setIsCreatingList(false);
    }
  };

  return (
    <>
      <button
        ref={triggerButtonRef}
        type="button"
        disabled={addToWishlistDisabled}
        onClick={() => {
          if (addToWishlistDisabled) return;
          onOpen();
        }}
        aria-label={translate('pdp.add-to-purchase-list')}
        className={classnames(
          'text-primary hover:bg-primary hover:border-primary inline-flex w-12 shrink-0 items-center justify-center self-stretch rounded-lg border border-gray-300 bg-white transition-colors hover:text-white',
          { 'cursor-not-allowed opacity-50': addToWishlistDisabled },
        )}
      >
        <QueueListIcon className="size-5" aria-hidden />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="animate-in fade-in absolute inset-0 bg-black/40 duration-200"
            onClick={handleClose}
            aria-hidden
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="animate-in slide-in-from-right absolute top-0 right-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl duration-300 ease-out"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
              <h2 id={titleId} className="text-22 text-primary font-bold">
                {translate('pdp.add-to-purchase-list')}
              </h2>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={handleClose}
                aria-label={translate('common.close')}
                className="text-primary p-1 transition-opacity hover:opacity-70"
              >
                <XMarkIcon className="size-6" aria-hidden />
              </button>
            </div>

            {/* Product summary */}
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <p className="text-13 tracking-wide text-gray-500 uppercase">{translate('wishlist.adding-product')}</p>
              {productName && <p className="text-15 mt-1 line-clamp-2 font-semibold text-gray-800">{productName}</p>}
              {productSku && (
                <p className="text-12 mt-0.5 text-gray-400">
                  {translate('common.cat-no')}: {productSku}
                </p>
              )}
            </div>

            {lists.length > 0 ? (
              <>
                {/* List */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <p className="text-13 mb-3 font-semibold tracking-wide text-gray-600 uppercase">
                    {translate('wishlist.your-lists')}
                  </p>
                  <div className="flex flex-col gap-2">
                    {lists.map(({ label, id, productIsInWishlist }) => {
                      const isSelected = !!productIsInWishlist;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => handleToggle(id)}
                          disabled={!!loadingId}
                          className={classnames(
                            'flex w-full items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition-all',
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 bg-white hover:border-gray-300',
                            { 'opacity-60': loadingId === id },
                          )}
                          data-testid={`list-row-${label}`}
                        >
                          <p
                            className={classnames(
                              'text-15 font-semibold',
                              isSelected ? 'text-primary' : 'text-gray-800',
                            )}
                          >
                            {label}
                          </p>
                          {isSelected && (
                            <div className="flex items-center gap-2">
                              {addedId === id && (
                                <span className="text-12 font-medium text-green-600">
                                  {translate('wishlist.added')}
                                </span>
                              )}
                              <CheckIcon className="text-primary size-5 shrink-0" aria-hidden />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Footer — Done only */}
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <Button variant="primary" size="m" onClick={handleClose} className="w-full">
                    {translate('product.done')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Create form */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <p className="text-16 mb-4 font-semibold text-gray-700">{translate('product.add-to-new-list')}</p>
                  {/* EWM-760: react-hooks/refs false positive on the next line — see docs/superpowers/plans/ewm-760-disables.md */}
                  {/* eslint-disable-next-line react-hooks/refs -- EWM-760: react-hook-form's documented handleSubmit(fn) usage; handleCreateSubmit owns no ref */}
                  <form id={formId} onSubmit={handleSubmit(handleCreateSubmit)} noValidate>
                    <div className="flex flex-col gap-4">
                      <Input
                        label={translate('common.name')}
                        containerClassName="w-full"
                        error={errors.name?.message}
                        {...register('name', { required: translate('common.fieldIsRequired') })}
                        required
                      />
                      <TextArea
                        label={translate('common.description')}
                        showOptionalLabel
                        className="h-[140px] w-full"
                        {...register('description')}
                      />
                    </div>
                  </form>
                </div>

                {/* Footer — Save & Add */}
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <Button size="m" type="submit" form={formId} loading={isCreatingList} className="w-full">
                    {translate('product.save-and-add')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ShoppingListCTA;
